import { Injectable } from '@angular/core';
import {
  ImageResolutionInterface,
  OrganizationBodyInterface,
  OrganizationFileTypeEnum,
  OrganizationFileUploadInterface,
  OrganizationLightInterface,
} from '@sport-activities/sdk';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { createHttpParams } from '~/core/helpers/create-http-params/create-http-params.helper';

import { OrganizationService } from '~/core/services/organization/organization.service';
import {
  PayoutInvoicesApiResponseInterface,
  PayoutsFilterParamsInterface,
} from '~/organizer/payout-invoices/interfaces/payout-invoice.interface';

import { buildFormDataForMediaUpload } from '~/organizer/shared/helpers/media/media.helper';

@Injectable({
  providedIn: 'root',
})
export class OrganizerSharedOrganizationService extends OrganizationService {
  public organization: OrganizationLightInterface;

  private builtOrganizationPayload(
    partialOrganization: Partial<OrganizationBodyInterface>
  ): OrganizationBodyInterface {
    return {
      ...(this.organization as OrganizationBodyInterface),
      ...(partialOrganization as Partial<OrganizationBodyInterface>),
    };
  }

  public addMedia(
    organizationIdentifier: string,
    media: Blob,
    name: string,
    fileTypes: OrganizationFileTypeEnum[],
    resolutions?: ImageResolutionInterface[]
  ): Observable<OrganizationFileUploadInterface> {
    const formData = buildFormDataForMediaUpload(
      media,
      name,
      fileTypes,
      resolutions
    );

    return this.httpClient.post<OrganizationFileUploadInterface>(
      `${this.organizationUrl}/${organizationIdentifier}/medias`,
      formData
    );
  }

  public getOrganizationById(
    organizationId: string
  ): Observable<OrganizationLightInterface> {
    return super
      .getOrganizationById(organizationId)
      .pipe(
        map(
          (organization: OrganizationLightInterface) =>
            (this.organization = organization)
        )
      );
  }

  public getOrganizationPayoutInvoices(
    organizationId: string,
    search: string,
    limit: number,
    page = 1
  ): Observable<PayoutInvoicesApiResponseInterface> {
    let payoutsFilterParams: PayoutsFilterParamsInterface = {
      from: (page - 1) * limit,
      limit: limit,
    };

    if (search) {
      payoutsFilterParams = {
        ...payoutsFilterParams,
        search: search,
      };
    }

    const params = createHttpParams(payoutsFilterParams);

    return this.httpClient.get<PayoutInvoicesApiResponseInterface>(
      `${this.organizationUrl}/${organizationId}/payouts`,
      { params }
    );
  }

  public hasOrganization(): boolean {
    return Boolean(this.organization);
  }

  public patchOrganization(
    partialOrganization: Partial<OrganizationBodyInterface>
  ): Observable<OrganizationLightInterface> {
    const organization: OrganizationBodyInterface =
      this.builtOrganizationPayload(partialOrganization);

    return this.updateOrganization(organization);
  }

  public postOrganization(
    organization: Partial<OrganizationBodyInterface>
  ): Observable<OrganizationLightInterface> {
    return this.httpClient
      .post<OrganizationLightInterface>(this.organizationUrl, organization)
      .pipe(
        map(
          (createdOrganization: OrganizationLightInterface) =>
            (this.organization = createdOrganization)
        )
      );
  }

  public updateOrganization(
    organization: OrganizationBodyInterface
  ): Observable<OrganizationLightInterface> {
    return this.updateOrganizationById(
      this.organization.identifier,
      organization
    );
  }

  public updateOrganizationById(
    organizationId: string,
    organization: OrganizationBodyInterface
  ): Observable<OrganizationLightInterface> {
    return this.httpClient
      .put<OrganizationLightInterface>(
        `${this.organizationUrl}/${organizationId}`,
        organization
      )
      .pipe(
        map(
          (updatedOrganization: OrganizationLightInterface) =>
            (this.organization = updatedOrganization)
        )
      );
  }
}
